import type { TranslationKey } from "./ko.js";

export const en: Record<TranslationKey, string> = {
  // Common
  "app.name": "ooo.codes",
  "app.subtitle": "URL Shortener & QR Generator",
  "common.loading": "Loading...",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.copy": "Copy",
  "common.copied": "Copied",

  // Login
  "login.title": "Sign in with Passkey",
  "login.button": "Sign in with Passkey",
  "login.authenticating": "Authenticating...",
  "login.passkey_info": "This service uses passwordless authentication via Passkeys (WebAuthn).",
  "login.first_time": "First time? Set up your account",
  "login.have_invite": "Have an invitation code?",
  "login.invite_placeholder": "Enter invitation code",
  "login.join": "Join",
  "login.back_to_login": "Back to login",
  "login.auth_cancelled": "Authentication was cancelled or not allowed.",
  "login.failed": "Login failed",
  "login.or": "or",
  "login.signup_button": "Sign Up",
  "login.signup_title": "Create Account",
  "login.signup_subtitle": "Sign up easily with a passkey.",
  "login.usage_today": "Today's Requests",
  "login.used": "used",
  "login.pending_approval": "Your account is pending approval. You can log in after an admin approves your account.",
  "login.account_suspended": "Your account has been suspended. Please contact the administrator.",
  "login.account_banned": "Your account has been banned.",
  "login.signup_success": "Registration complete! You can log in after admin approval.",

  // Setup
  "setup.title": "Welcome! Set up your account",
  "setup.subtitle": "Create the owner account to get started.",
  "setup.username": "Username",
  "setup.display_name": "Display Name",
  "setup.button": "Create Account with Passkey",
  "setup.setting_up": "Setting up...",
  "setup.already_have_account": "Already have an account? Sign in",
  "setup.cancelled": "Registration was cancelled or not allowed.",
  "setup.failed": "Setup failed",

  // Invite
  "invite.verifying": "Verifying invitation...",
  "invite.invalid_title": "Invalid or Expired Invitation",
  "invite.invalid_message": "This invitation link is no longer valid. Please ask the administrator for a new invitation.",
  "invite.invited": "You have been invited to join",
  "invite.create_account": "Create your account",
  "invite.for": "Invitation for",
  "invite.username": "Username",
  "invite.username_placeholder": "Choose a username",
  "invite.display_name": "Display Name",
  "invite.display_name_placeholder": "Your name",
  "invite.button": "Create Account with Passkey",
  "invite.setting_up": "Setting up...",
  "invite.passkey_info": "You will be prompted to create a Passkey for passwordless authentication.",
  "invite.cancelled": "Registration was cancelled or not allowed.",
  "invite.failed": "Registration failed",

  // Invitations
  "invitations.title": "Invitation Management",
  "invitations.send": "Send Invitation",
  "invitations.email_placeholder": "Enter email address",
  "invitations.send_button": "Send Invite",
  "invitations.sending": "Sending...",
  "invitations.list_title": "Invitation List",
  "invitations.empty": "No invitations sent yet.",
  "invitations.status_pending": "Pending",
  "invitations.status_accepted": "Accepted",
  "invitations.status_expired": "Expired",
  "invitations.copy_link": "Copy Link",
  "invitations.revoke": "Revoke",
  "invitations.revoke_confirm": "Revoke this invitation?",
  "invitations.created": "Created",
  "invitations.expires": "Expires",
  "invitations.owner_only": "Invitation management is only available to owners.",

  // Create Link
  "create.advanced_settings": "Advanced Settings",
  "create.qr_preview": "QR Code Preview",
  "create.default_expiry_info": "Default expiry: 7 days",

  // Navigation
  "nav.dashboard": "Dashboard",
  "nav.create_link": "Create Link",
  "nav.invitations": "Invitations",
  "nav.settings": "Settings",
  "nav.logout": "Log out",
  "nav.signed_in_as": "Signed in as",
  "nav.toggle_menu": "Toggle menu",
  "nav.toggle_theme": "Toggle theme",

  // Dashboard
  "dashboard.title": "Your Links",
  "dashboard.search": "Search links...",
  "dashboard.create_link": "Create Link",
  "dashboard.loading": "Loading links...",
  "dashboard.no_results": "No links found matching",
  "dashboard.empty": "No links yet. Create your first short link!",
  "dashboard.clicks": "clicks",
  "dashboard.inactive": "Inactive",
  "dashboard.previous": "Previous",
  "dashboard.next": "Next",
  "dashboard.page_of": "Page {page} of {total}",
  "dashboard.copy_url": "Copy short URL",

  // Create Link
  "create.title": "Create Short Link",
  "create.url_label": "Destination URL *",
  "create.custom_slug": "Custom URL",
  "create.slug_invalid": "Only letters, numbers, hyphens, and underscores are allowed.",
  "create.slug_taken": "This slug is already taken.",
  "create.slug_available": "This slug is available!",
  "create.slug_auto": "A random slug will be auto-generated.",
  "create.title_label": "Title",
  "create.expires_label": "Expires At",
  "create.remove_expiration": "Remove expiration",
  "create.preview": "Preview",
  "create.button": "Create Short Link",
  "create.creating": "Creating...",
  "create.failed": "Failed to create link",
  "create.title_tooltip": "A name to easily identify this link in your dashboard.",
  "create.slug_tooltip": "Set a custom short path after ooo.codes/. Leave empty for auto-generation.",
  "create.expires_tooltip": "The link will be deactivated after this date. Maximum 30 days.",
  "create.expires_max_30": "Maximum 30 days allowed.",

  // Link Detail
  "detail.loading": "Loading...",
  "detail.not_found": "Link not found",
  "detail.back": "Back to Dashboard",
  "detail.inactive": "Inactive",
  "detail.analytics": "Analytics",
  "detail.edit": "Edit",
  "detail.cancel": "Cancel",
  "detail.delete": "Delete",
  "detail.confirm_delete": "Confirm?",
  "detail.yes_delete": "Yes, Delete",
  "detail.no": "No",
  "detail.short_url": "Short URL",
  "detail.destination": "Destination",
  "detail.clicks": "Clicks",
  "detail.created": "Created",
  "detail.expires": "Expires",
  "detail.status": "Status",
  "detail.active": "Active",
  "detail.edit_link": "Edit Link",
  "detail.destination_url": "Destination URL",
  "detail.title_label": "Title",
  "detail.expires_at": "Expires At",
  "detail.remove_expiration": "Remove expiration",
  "detail.active_toggle": "Active",
  "detail.save_changes": "Save Changes",
  "detail.saving": "Saving...",
  "detail.update_failed": "Failed to update link",
  "detail.copy_tooltip": "Copy to clipboard",

  // Analytics
  "analytics.title": "Analytics",
  "analytics.loading": "Loading analytics...",
  "analytics.no_data": "No analytics data available.",
  "analytics.last_7": "Last 7 days",
  "analytics.last_30": "Last 30 days",
  "analytics.last_90": "Last 90 days",
  "analytics.total_clicks": "Total Clicks",
  "analytics.today": "Today",
  "analytics.this_week": "This Week",
  "analytics.this_month": "This Month",
  "analytics.over_time": "Clicks Over Time",
  "analytics.referrers": "Top Referrers",
  "analytics.countries": "Top Countries",
  "analytics.devices": "Devices",
  "analytics.browsers": "Browsers",
  "analytics.empty": "No click data yet. Share your link to start collecting analytics.",

  // Settings
  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.username": "Username",
  "settings.display_name": "Display Name",
  "settings.role": "Role",
  "settings.member_since": "Member Since",
  "settings.api_keys": "API Keys",
  "settings.api_keys_desc": "Create API keys to access the ooo.codes API programmatically.",
  "settings.key_placeholder": "Key name (e.g., CI/CD Pipeline)",
  "settings.create_key": "Create Key",
  "settings.key_created": "API key created! Copy it now -- it will not be shown again.",
  "settings.no_keys": "No API keys yet.",
  "settings.revoke": "Revoke",
  "settings.created": "Created",
  "settings.last_used": "Last used",
  "settings.invite_user": "Invite User",
  "settings.invite_desc": "Send an invitation to allow someone to create an account.",
  "settings.send_invite": "Send Invite",
  "settings.invite_created": "Invitation created! Share this link:",
  "settings.create_key_failed": "Failed to create API key",
  "settings.revoke_failed": "Failed to revoke key",
  "settings.invite_failed": "Failed to send invitation",

  // QR Customizer
  "qr.title": "QR Code",
  "qr.foreground": "Foreground",
  "qr.background": "Background",
  "qr.size": "Size",
  "qr.error_correction": "Error Correction",
  "qr.ec_low": "Low",
  "qr.ec_med": "Med",
  "qr.ec_high": "High",
  "qr.ec_max": "Max",
  "qr.ec_info": "Higher correction allows QR to be scanned even if partially damaged.",
  "qr.save": "Save Configuration",
  "qr.saving": "Saving...",
  "qr.saved": "Saved!",

  // Admin
  "admin.title": "Admin Console",
  "admin.dashboard": "Dashboard",
  "admin.users": "User Management",
  "admin.stats": "Statistics",
  "admin.total_users": "Total Users",
  "admin.active_users": "Active Users",
  "admin.pending_users": "Pending Approval",
  "admin.total_links": "Total Links",
  "admin.total_clicks": "Total Clicks",
  "admin.today_signups": "Today's Signups",
  "admin.user_detail": "User Detail",
  "admin.status_pending": "Pending",
  "admin.status_active": "Active",
  "admin.status_suspended": "Suspended",
  "admin.status_banned": "Banned",
  "admin.approve": "Approve",
  "admin.reject": "Reject",
  "admin.suspend": "Suspend",
  "admin.ban": "Ban",
  "admin.activate": "Activate",
  "admin.daily_limit": "Daily Link Limit",
  "admin.daily_limit_unlimited": "Unlimited",
  "admin.links_today": "Links Today",
  "admin.status_reason": "Reason",
  "admin.status_reason_placeholder": "Enter reason for suspend/ban",
  "admin.confirm_suspend": "Suspend this user?",
  "admin.confirm_ban": "Permanently ban this user?",
  "admin.link_count": "Links",
  "admin.member_since": "Member Since",
  "admin.role": "Role",
  "admin.status": "Status",
  "admin.actions": "Actions",
  "admin.no_users": "No users found.",
  "admin.all": "All",
  "admin.filter_status": "Filter by Status",
  "admin.back": "Back to list",
  "admin.save_limit": "Save",
  "admin.console": "Admin",
  "admin.role_owner": "Owner",
  "admin.role_member": "Member",
  "admin.change_role": "Change Role",
  "admin.confirm_role_change": "Change this user's role?",
};
